import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import { css, styled } from 'styled-components';
import { useIntersectionObserver, useOnClickOutside, } from '@hivebrite/react-hooks';
import { getNextFocusableElement, onEnterOrSpacePress } from '@hivebrite/utils';
import { Box } from '@design-system/box';
import { ScreenReadersContent } from '@design-system/screen-readers-content';
import { focusOutline, useTheme } from '@design-system/theme';
const MAX_REPOSITION = 3;
export const DropdownBase = ({ buttonWrapperProps = {}, children, dataTestId, isDisabled = false, isFullWidth = false, maxHeight, position: initialPosition = 'left', renderContent, title, wrapperStyles = {}, }) => {
    const theme = useTheme();
    const doRepositionCountRef = useRef(0);
    const wrapperRef = useRef(null);
    const wrapperClickRef = useRef(null);
    const wrapperItemsRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(initialPosition);
    const [xOffset, setXOffset] = useState(0);
    const [positionReady, setPositionReady] = useState(false);
    const listId = useId();
    const offsetPx = `${xOffset}px`;
    const handleClose = useCallback((origin) => {
        setIsOpen(false);
        if (origin === 'DropdownItem' && wrapperClickRef.current) {
            wrapperClickRef.current.focus();
        }
    }, []);
    const handleTriggerClick = useCallback((event) => {
        if (!isOpen) {
            event.stopPropagation();
        }
        event.preventDefault();
        setIsOpen(!isOpen);
    }, [isOpen]);
    const handleIntersectionObserver = useCallback(([entry]) => {
        if (entry.intersectionRatio === 0 ||
            entry.intersectionRatio === 1 ||
            doRepositionCountRef.current >= MAX_REPOSITION) {
            setPositionReady(true);
            return;
        }
        doRepositionCountRef.current++;
        if (doRepositionCountRef.current === MAX_REPOSITION) {
            setXOffset(entry.intersectionRect.width -
                entry.boundingClientRect.width -
                theme.space[2]);
        }
        else {
            setPosition((prev) => (prev === 'left' ? 'right' : 'left'));
        }
    }, [theme]);
    const itemsTransitions = useTransition(isOpen, {
        enter: {
            opacity: 1,
            pointerEvents: 'all',
            y: 0,
        },
        from: {
            opacity: 0,
            pointerEvents: 'none',
            y: -theme.space[1],
        },
        leave: {
            opacity: 0,
            pointerEvents: 'none',
            y: -theme.space[1],
        },
    });
    useEffect(() => {
        const handleFocus = () => {
            if (isOpen &&
                wrapperRef.current &&
                !wrapperRef.current.contains(document.activeElement)) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('focus', handleFocus, true);
        }
        return () => document.removeEventListener('focus', handleFocus, true);
    }, [handleClose, isOpen]);
    useEffect(() => {
        const handlePress = (e) => {
            var _a;
            if (isOpen && e.key === 'Escape') {
                handleClose();
                (_a = wrapperClickRef === null || wrapperClickRef === void 0 ? void 0 : wrapperClickRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
        const handleArrowPress = (e) => {
            var _a;
            if (!isOpen) {
                return;
            }
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
                wrapperItemsRef.current) {
                const step = e.key === 'ArrowDown' ? 1 : -1;
                const nextElement = getNextFocusableElement(wrapperItemsRef.current, step);
                e.preventDefault();
                (_a = nextElement === null || nextElement === void 0 ? void 0 : nextElement.focus) === null || _a === void 0 ? void 0 : _a.call(nextElement);
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handlePress, true);
            document.addEventListener('keydown', handleArrowPress, true);
        }
        return () => {
            document.removeEventListener('keydown', handlePress, true);
            document.removeEventListener('keydown', handleArrowPress, true);
        };
    }, [handleClose, isOpen]);
    useEffect(() => {
        if (wrapperItemsRef.current) {
            const clientRect = wrapperItemsRef.current.getBoundingClientRect();
            if (document.documentElement.clientWidth <=
                clientRect.left + clientRect.width) {
                setPosition('left');
            }
        }
    }, [position]);
    useOnClickOutside(wrapperRef, handleClose, isOpen);
    useIntersectionObserver(wrapperItemsRef, handleIntersectionObserver);
    return (_jsxs(Wrapper, { "$isOpen": isOpen, "data-testid": dataTestId, ref: wrapperRef, style: wrapperStyles, children: [_jsxs(WrapperClick, Object.assign({ "$isDisabled": isDisabled, "data-testid": dataTestId && `${dataTestId}-button`, ref: wrapperClickRef, width: isFullWidth ? '100%' : undefined }, (!isDisabled && {
                'aria-controls': listId,
                'aria-expanded': isOpen,
                onClick: handleTriggerClick,
                onKeyDown: onEnterOrSpacePress(handleTriggerClick),
                role: 'button',
                tabIndex: 0,
            }), buttonWrapperProps, { children: [_jsx(ScreenReadersContent, { children: title }), typeof children === 'function' ? children({ isOpen }) : children] })), itemsTransitions((_a, item) => {
                var { opacity, y } = _a, styles = __rest(_a, ["opacity", "y"]);
                return item && (_jsx(AnimatedDiv, { id: listId, offsetPx: offsetPx, position: position, style: Object.assign(Object.assign({}, styles), { opacity: positionReady ? opacity : 0, transform: y.to((vertical) => `translate3d(0, ${vertical}%, 0)`) }), children: renderContent({
                        contentMaxHeight: maxHeight,
                        handleClose,
                        isOpen,
                        wrapperItemsRef,
                    }) }));
            })] }));
};
const Wrapper = styled(Box).attrs(() => ({
    display: 'inline-flex',
})) `
  position: relative;
  z-index: ${(props) => (props.$isOpen ? 11 : 10)};
`;
const WrapperClick = styled(Box).attrs(({ px, py }) => ({
    px,
    py,
})) `
  cursor: ${(props) => (props.$isDisabled ? 'auto' : 'pointer')};
  position: relative;
  user-select: none;
  z-index: 1;

  &:focus {
    outline: none;
  }

  ${focusOutline};
`;
const AnimatedDiv = styled(animated.div).withConfig({
    shouldForwardProp: (prop) => !['position', 'offsetPx'].includes(prop),
}) `
  ${({ offsetPx, position }) => css `
    left: ${position === 'right' ? offsetPx : 'auto'};
    position: absolute;
    right: ${position === 'left' ? offsetPx : 'auto'};
    top: 100%;
  `}
`;
//# sourceMappingURL=DropdownBase.js.map